import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QrCode, Filter } from "lucide-react";
import { toast } from "sonner";

const SOURCE_COLORS = {
  portal: "bg-sky-100 text-sky-700",
  quiz: "bg-emerald-100 text-emerald-700",
  qr: "bg-violet-100 text-violet-700",
};

export default function Attendance() {
  const { branding } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";
  const [data, setData] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", source: "all", department_id: "all", emp_id: "" });
  const [depts, setDepts] = useState([]);

  useEffect(() => { api.get("/masters/departments").then(r => setDepts(r.data)).catch(()=>{}); }, []);

  async function load() {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.source !== "all") params.source = filters.source;
      if (filters.department_id !== "all") params.department_id = filters.department_id;
      if (filters.emp_id.trim()) params.emp_id = filters.emp_id.trim();
      const r = await api.get("/attendance", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const clearFilters = () => {
    setFilters({ date_from: "", date_to: "", source: "all", department_id: "all", emp_id: "" });
    setPage(1);
  };

  const openQR = () => window.open(`${window.location.origin}/attend`, "_blank");
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const start = data.total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(data.total, page * pageSize);

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: brand }}>Attendance</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Attendance Records</h1>
          <p className="text-slate-500 mt-1">Unified log of every attendance — portal mark, quiz auto-attendance, and QR-based check-ins.</p>
        </div>
        <Button onClick={openQR} className="rounded-xl" style={{ background: brand, color: "white" }} data-testid="open-qr-page">
          <QrCode className="h-4 w-4 mr-1.5"/>Open Public Attendance Page
        </Button>
      </header>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5"><Filter className="h-3.5 w-3.5"/>Filters</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <Input type="date" value={filters.date_from} onChange={e=>setFilters({...filters, date_from: e.target.value})} data-testid="att-filter-from"/>
            </div>
            <div>
              <Label className="text-xs text-slate-500">To</Label>
              <Input type="date" value={filters.date_to} onChange={e=>setFilters({...filters, date_to: e.target.value})} data-testid="att-filter-to"/>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Source</Label>
              <Select value={filters.source} onValueChange={v=>setFilters({...filters, source: v})}>
                <SelectTrigger data-testid="att-filter-source"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="quiz">Quiz (auto)</SelectItem>
                  <SelectItem value="qr">QR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Department</Label>
              <Select value={filters.department_id} onValueChange={v=>setFilters({...filters, department_id: v})}>
                <SelectTrigger data-testid="att-filter-dept"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {depts.map(d=> <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Employee ID</Label>
              <Input value={filters.emp_id} onChange={e=>setFilters({...filters, emp_id: e.target.value})} placeholder="EMP001" data-testid="att-filter-empid"/>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={()=>{ setPage(1); load(); }} className="bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="att-apply"><Filter className="h-4 w-4 mr-1.5"/>Apply</Button>
            <Button variant="outline" onClick={clearFilters} className="rounded-xl" data-testid="att-clear">Clear</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="text-sm">
              <span className="font-bold text-slate-900">{data.total}</span>
              <span className="text-slate-500"> records · showing {start}–{end}</span>
              {loading && <span className="text-slate-400 ml-2 italic">loading…</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="att-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Employee</th>
                  <th className="py-3 px-4">Session / Quiz</th>
                  <th className="py-3 px-4">Phase</th>
                  <th className="py-3 px-4">Source</th>
                  <th className="py-3 px-4">Marked At</th>
                </tr>
              </thead>
              <tbody>
                {data.data.length === 0 && (
                  <tr><td colSpan="6" className="py-10 text-center text-slate-400">No attendance records for current filters.</td></tr>
                )}
                {data.data.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-4 font-mono text-xs">{r.date}</td>
                    <td className="py-3 px-4">
                      <div className="font-bold text-slate-900">{r.emp_name}</div>
                      <div className="text-xs text-slate-400">{r.emp_id}</div>
                    </td>
                    <td className="py-3 px-4">{r.questionnaire_title || <span className="text-slate-300 italic">— general —</span>}</td>
                    <td className="py-3 px-4">
                      {r.phase ? <Badge variant="secondary" className="bg-violet-50 text-violet-700 capitalize">{r.phase}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary" className={SOURCE_COLORS[r.source] || "bg-slate-100 text-slate-600"}>{r.source}</Badge>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">{r.marked_at ? new Date(r.marked_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            <div>Page {page} / {totalPages}</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="h-7" data-testid="att-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)} className="h-7" data-testid="att-next">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
